import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDemo } from './DemoProvider';
import { useReaction } from '../reactions/ReactionProvider';
import { DEMO_TOUR_EXIT, DEMO_TOUR_STOPS, tourAppliesTo, tourIsRunning, tourStopAt } from './demoTour2243';
import { ADVENTURE_CHOICES } from './adventureEngine';
import type { ReactionEventId } from '../reactions/reactionCatalog';
import type { DemoStageId } from './demoExperience';

const stageReactions: Record<DemoStageId, ReactionEventId> = {
  'first-snow': 'mission.start',
  'goal-road': 'choice.selected',
  basecamp: 'record.saved',
  weather: 'quiz.correct',
  library: 'world.opened',
  fridge: 'fridge.stored',
  family: 'family.cheer',
  summit: 'day.completed',
};

/**
 * The tour, drawn over the app rather than in place of it.
 *
 * It is mounted once at the router root, not by any screen, so moving between
 * stops is ordinary navigation and every stop shows the real screen with the
 * real data of the demo account. Nothing here renders a copy of a screen.
 */
export function DemoTourOverlay() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { session, setStage, setSelection, completeStage, next, previous, setAutoPlaying, reset } = useDemo();
  const { emit } = useReaction();
  const [collapsed, setCollapsed] = useState(false);
  const [seconds, setSeconds] = useState(0);
  // Which stop the overlay last drove the browser to. Without it the effect
  // would drag the visitor back every time they tapped something on the screen
  // being toured — the screen would become unusable, which is the opposite of
  // the point of touring a real one.
  const drivenTo = useRef(-1);

  const running = tourIsRunning(session) && tourAppliesTo(pathname);
  const stop = tourStopAt(session.currentStage);
  const choices = ADVENTURE_CHOICES[stop.id];
  const selection = session.selections[stop.id] ?? '';
  const done = session.completedStageIds.includes(stop.id);

  useEffect(() => {
    if (!running) { drivenTo.current = -1; return; }
    if (drivenTo.current === session.currentStage) return;
    drivenTo.current = session.currentStage;
    if (pathname !== stop.route) navigate(stop.route);
  }, [navigate, pathname, running, session.currentStage, stop.route]);

  useEffect(() => { setSeconds(stop.durationSeconds); }, [stop.durationSeconds, stop.id]);

  useEffect(() => {
    if (!running || !session.autoPlaying) return;
    const ticker = window.setInterval(() => setSeconds((value) => Math.max(0, value - 1)), 1000);
    const timer = window.setTimeout(() => {
      const picked = selection || choices[0].label;
      completeStage(stop.id, picked);
      void emit(stageReactions[stop.id], { silent: !session.soundRequested });
      if (session.currentStage < DEMO_TOUR_STOPS.length - 1) next();
      else { setAutoPlaying(false); navigate(DEMO_TOUR_EXIT); }
    }, stop.durationSeconds * 1000);
    return () => { window.clearInterval(ticker); window.clearTimeout(timer); };
  }, [choices, completeStage, emit, navigate, next, running, selection, session.autoPlaying, session.currentStage, session.soundRequested, setAutoPlaying, stop]);

  if (!running) return null;

  const advance = () => {
    const picked = selection || choices[0].label;
    if (!done) {
      completeStage(stop.id, picked);
      void emit(stageReactions[stop.id], { silent: !session.soundRequested });
    }
    if (session.currentStage < DEMO_TOUR_STOPS.length - 1) next();
    else navigate(DEMO_TOUR_EXIT);
  };

  const last = session.currentStage === DEMO_TOUR_STOPS.length - 1;

  return <aside className={`demo-tour-2-24-3 ${collapsed ? 'collapsed' : ''}`} aria-label="MRM 안내 투어">
    <header className="demo-tour-head-2-24-3">
      <span className="demo-tour-count-2-24-3">{stop.order} / {DEMO_TOUR_STOPS.length}</span>
      <div>
        <strong>{stop.place}</strong>
        <small>{stop.title} · {stop.emotion}</small>
      </div>
      <button type="button" className="demo-tour-fold-2-24-3" onClick={() => setCollapsed((value) => !value)}
        aria-expanded={!collapsed}>{collapsed ? '안내 펼치기' : '접기'}</button>
    </header>

    {!collapsed && <div className="demo-tour-body-2-24-3">
      <p className="demo-tour-focus-2-24-3">{stop.focus}</p>

      <nav className="demo-tour-dots-2-24-3" aria-label="투어 진행">
        {DEMO_TOUR_STOPS.map((item, index) => <button key={item.id} type="button"
          className={`${index === session.currentStage ? 'current ' : ''}${session.completedStageIds.includes(item.id) ? 'done' : ''}`}
          aria-label={`${item.order}번째 장면 ${item.place}`} aria-current={index === session.currentStage ? 'step' : undefined}
          onClick={() => setStage(index)}>{session.completedStageIds.includes(item.id) ? '✓' : item.order}</button>)}
      </nav>

      <div className="demo-tour-choices-2-24-3" role="group" aria-label={stop.actionLabel}>
        <small>{stop.actionLabel}</small>
        {choices.map((choice) => <button key={choice.label} type="button"
          className={selection === choice.label ? 'selected' : ''} aria-pressed={selection === choice.label}
          onClick={() => setSelection(stop.id, choice.label)}>{choice.label}</button>)}
      </div>
    </div>}

    <div className="demo-tour-controls-2-24-3">
      <button type="button" onClick={previous} disabled={session.currentStage === 0} aria-label="이전 장면">‹</button>
      <button type="button" className={session.autoPlaying ? 'active' : ''} onClick={() => setAutoPlaying(!session.autoPlaying)}>
        {session.autoPlaying ? `멈춤 · ${seconds}초` : '자동 재생'}
      </button>
      <button type="button" className="demo-tour-next-2-24-3" onClick={advance}>{last ? '결과 보기' : '다음 장면'} ›</button>
      <button type="button" className="demo-tour-quit-2-24-3" onClick={() => { setAutoPlaying(false); reset(); navigate('/demo'); }}>종료</button>
    </div>
  </aside>;
}
